import React, { useEffect, useState } from 'react';
import { Typography, Box, Button, Grid, Dialog, DialogContent, DialogActions, Toolbar } from '@mui/material';
import { Form } from 'react-final-form';
import { AxiosError } from 'axios';
import DynamicField from '../../components/DynamicField';
import {
  getStripeConnectionStatus,
  connectStripe,
  saveStripeSettings,
  runStripeAction,
  disconnectStripe,
  ConnectionStatus,
  ConnectionStatusType,
  StripeField,
  ConnectorAction,
} from './stripe_service';
import { agenticDashboardStyles } from '../../components/agentic_dashboard_styles';
import ResultDialog from '../../components/ResultDialog';
import ConfirmDialog from '../../components/confirm_dialog';
import { pageContentGap } from '../../theme';

interface StripeDashboardProps {
  setTitle?: (title: string) => void;
}

function isAxiosError(error: unknown): error is AxiosError {
  return typeof error === 'object' && error !== null && (error as AxiosError).isAxiosError === true;
}

const styles = {
  ...agenticDashboardStyles,
  stripeLogo: {
    height: '40px',
  },
};

const StripeDashboard: React.FC<StripeDashboardProps> = (props) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusType>(ConnectionStatus.NOT_CONNECTED);
  const [loading, setLoading] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [initialSettings, setInitialSettings] = useState<Record<string, string | null>>({});
  const [fields, setFields] = useState<StripeField[]>([]);
  const [oauthName, setOauthName] = useState<string | null>(null);
  const [actions, setActions] = useState<ConnectorAction[]>([]);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  useEffect(() => {
    if (props.setTitle) {
      props.setTitle('Stripe Agentic Dashboard');
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.setTitle]);

  const loadData = async () => {
    try {
      const statusResult = await getStripeConnectionStatus();
      setConnectionStatus(statusResult.status);
      setInitialSettings(statusResult.settings || {});
      setFields(statusResult.fields || []);
      setOauthName(statusResult.oauthName);
      setActions(statusResult.actions || []);
    } catch (error) {
      console.error('Error loading Stripe data:', error);
      setConnectionStatus(ConnectionStatus.ERROR);
    }
  };

  const handleOpenConnectDialog = () => {
    setShowConnectDialog(true);
  };

  const handleCloseConnectDialog = () => {
    setShowConnectDialog(false);
  };

  const handleConnectStripe = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      await connectStripe(values, oauthName!);
    } catch (error: unknown) {
      console.error('Error connecting to Stripe:', error);
      const msg = isAxiosError(error) ? error.response?.data || error.message : String(error);
      setResultMessage('Error: ' + msg);
      setShowResultDialog(true);
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectStripe(oauthName!);
      await loadData();
    } catch (error) {
      console.error('Error disconnecting Stripe:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values: Record<string, unknown>) => {
    try {
      await saveStripeSettings(values);
      await loadData();
    } catch (error: unknown) {
      console.error('Error saving Stripe settings:', error);
      const msg = isAxiosError(error) ? error.response?.data || error.message : String(error);
      setResultMessage('Error: ' + msg);
      setShowResultDialog(true);
    }
  };

  const handleRunAction = async (actionName: string, values: Record<string, unknown>) => {
    try {
      const result = await runStripeAction(actionName, values);
      setResultMessage(result);
      setShowResultDialog(true);
    } catch (error: unknown) {
      console.error('Error running action:', error);
      const msg = isAxiosError(error) ? error.response?.data || error.message : String(error);
      setResultMessage('Error: ' + msg);
      setShowResultDialog(true);
    }
  };

  const isConnected = connectionStatus === ConnectionStatus.CONNECTED;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.contentContainer}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box>
            <Typography sx={styles.pageTitle}>Stripe Agentic Dashboard</Typography>
          </Box>
          <img src="/images/connections/stripe.png" alt="Stripe" style={styles.stripeLogo} />
        </Box>

        {/* Subtitle */}
        <Typography sx={styles.pageSubtitle}>
          Manage your connection to Stripe Agentic Commerce. Activate your Stripe account to enable intelligent commerce
          experiences through Logicbroker.
        </Typography>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Connection Section */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={styles.connectionCard}>
              <Typography sx={styles.sectionTitle}>Stripe Connection</Typography>
              <Box sx={styles.statusRow}>
                <Typography sx={styles.statusLabel}>Status:</Typography>
                <Typography sx={isConnected ? styles.statusConnected : styles.statusNotConnected}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </Typography>
              </Box>
              <Typography sx={styles.description}>
                {isConnected
                  ? 'Your Stripe account is connected to Logicbroker. Agentic Commerce features are now enabled.'
                  : 'Your Stripe account is not yet connected to Logicbroker. You must complete the Stripe connection flow to activate Agentic Commerce.'}
              </Typography>
              {isConnected ? (
                <Box style={{ display: 'flex', gap: pageContentGap }}>
                  <Button
                    variant="contained"
                    sx={styles.connectButton}
                    onClick={handleOpenConnectDialog}
                    disabled={loading || !oauthName}
                  >
                    {loading ? 'Processing...' : 'Reconnect'}
                  </Button>
                  <Button
                    variant="contained"
                    sx={styles.disconnectButton}
                    onClick={() => setShowDisconnectConfirm(true)}
                    disabled={loading || !oauthName}
                  >
                    Disconnect
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  sx={styles.connectButton}
                  onClick={handleOpenConnectDialog}
                  disabled={loading || !oauthName}
                >
                  {loading ? 'Processing...' : 'Connect to Stripe'}
                </Button>
              )}
              <Typography sx={styles.helperText}>
                {isConnected
                  ? 'Reconnect to update your settings or disconnect to remove Stripe from Logicbroker.'
                  : 'This will open a dialog to configure your Stripe connection.'}
              </Typography>
            </Box>

            {/* Settings Section - Only shown when connected */}
            {isConnected && fields.length > 0 && (
              <Box sx={styles.settingsCard}>
                <Typography sx={styles.sectionTitle}>Connection Settings</Typography>
                <Form
                  onSubmit={handleSaveSettings}
                  initialValues={initialSettings}
                  render={({ handleSubmit, pristine, submitting, values }) => (
                    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                      {fields.map((field) => (
                        <DynamicField key={field.name} field={field} />
                      ))}
                      <Box style={{ marginTop: '16px', textAlign: 'left', display: 'flex', gap: pageContentGap }}>
                        <Button variant="contained" color="primary" type="submit" disabled={pristine || submitting}>
                          {submitting ? 'Saving...' : 'Save'}
                        </Button>
                        {actions.map((action) => (
                          <Button
                            key={action.Name}
                            variant="outlined"
                            onClick={() => handleRunAction(action.Name, values)}
                          >
                            {action.FriendlyName}
                          </Button>
                        ))}
                      </Box>
                    </form>
                  )}
                />
              </Box>
            )}

            {/* About Section */}
            <Box sx={{ ...styles.aboutCard, marginTop: '16px' }}>
              <Typography sx={styles.sectionTitle}>About Stripe Agentic Commerce</Typography>
              <Typography sx={{ ...styles.description, marginBottom: 0 }}>
                Connecting your Stripe account allows Logicbroker to process Agentic Commerce transactions on your
                behalf. This includes creating carts, validating orders, and capturing payments through Stripe. Your
                Stripe connection must be completed before these features can be enabled.
              </Typography>
            </Box>
          </Grid>

          {/* Right Column - Resources */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.resourcesCard}>
              <Typography sx={styles.sectionTitle}>Resources</Typography>
              <ul style={styles.resourcesList}>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>Stripe Agentic Commerce Overview</span>
                </li>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>Logicbroker Agentic Commerce Documentation</span>
                </li>
                <li style={styles.resourceItem}>
                  <span style={styles.resourceLink}>
                    <a href="https://help.logicbroker.com" target="_blank" rel="noopener noreferrer">
                      Contact Support
                    </a>
                  </span>
                </li>
              </ul>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Connect to Stripe Dialog */}
      <Dialog open={showConnectDialog} fullWidth maxWidth="sm" onClose={handleCloseConnectDialog}>
        <Toolbar className="lbtoolbar">Connect to Stripe</Toolbar>
        <Form
          onSubmit={handleConnectStripe}
          initialValues={initialSettings}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={styles.dialogContent}>
                {fields.map((field) => (
                  <DynamicField key={field.name} field={field} />
                ))}
              </DialogContent>
              <DialogActions>
                <Button variant="text" onClick={handleCloseConnectDialog}>
                  Cancel
                </Button>
                <Button variant="text" color="primary" type="submit" disabled={submitting || loading}>
                  {loading ? 'Processing...' : 'Connect'}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      {/* Result Dialog */}
      <ResultDialog open={showResultDialog} onClose={() => setShowResultDialog(false)} message={resultMessage} />

      <ConfirmDialog
        open={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnect}
        message="Are you sure you want to disconnect from Stripe? This will remove your Stripe connection from Logicbroker."
      />
    </Box>
  );
};

export default StripeDashboard;
